export const APP_TIME_ZONE = "America/Chicago";

export const getDateKeyInTimeZone = (
  value = new Date(),
  timeZone = APP_TIME_ZONE,
) => {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return value.trim();
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const dateParts = parts.reduce((acc, part) => {
    if (part.type !== "literal") {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});

  return `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
};

export const getTodayChicagoDateKey = () => getDateKeyInTimeZone();

export const FAR_FUTURE_DATE_FILTER_END = "2999-12-31";

export const addDaysToDateKey = (dateKey, days) => {
  if (!dateKey) return "";
  const [year, month, day] = dateKey.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + days, 12, 0, 0));
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");
};

export const getChicagoDateKeyWithOffset = (offsetDays = 0) =>
  addDaysToDateKey(getTodayChicagoDateKey(), offsetDays);

export const getNextMonthDateKey = (dateKey) => {
  if (!dateKey) return "";
  const [year, month, day] = dateKey.split("-").map(Number);
  const date = new Date(Date.UTC(year, month, day, 12, 0, 0));
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");
};

export const cleanDateFilters = (filters = {}) =>
  Object.entries(filters).reduce((acc, [key, value]) => {
    if (value !== null && typeof value !== "undefined" && value !== "") {
      acc[key] = value;
    }
    return acc;
  }, {});

export const isValidDateRange = ({
  encounterDateFrom = "",
  encounterDateTo = "",
} = {}) => {
  if (!encounterDateFrom || !encounterDateTo) return true;
  return encounterDateFrom <= encounterDateTo;
};

export const DEFAULT_EMPTY_DATE_FILTERS = {
  encounterDateFrom: "",
  encounterDateTo: "",
};

export const getDefaultDateFiltersTodayToNextMonth = () => {
  const today = getTodayChicagoDateKey();
  return {
    encounterDateFrom: today,
    encounterDateTo: getNextMonthDateKey(today),
  };
};

export const readStoredDateFilters = (storageKey, fallback) => {
  try {
    if (typeof sessionStorage === "undefined") return fallback;
    const stored = sessionStorage.getItem(storageKey);
    if (!stored) return fallback;
    const parsed = JSON.parse(stored);
    return {
      encounterDateFrom: parsed?.encounterDateFrom || fallback.encounterDateFrom || "",
      encounterDateTo: parsed?.encounterDateTo || fallback.encounterDateTo || "",
    };
  } catch (e) {
    console.error("Error reading date filters:", e);
    return fallback;
  }
};

export const clearStoredDateFilters = (storageKey) => {
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.removeItem(storageKey);
  } catch (e) {
    console.error("Error clearing date filters:", e);
  }
};

export const readReturnDateFilters = (
  storageKey,
  fallback,
  shouldRestore,
) => {
  if (shouldRestore) {
    return readStoredDateFilters(storageKey, fallback);
  }

  clearStoredDateFilters(storageKey);
  return fallback;
};

export const writeStoredDateFilters = (storageKey, filters) => {
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem(storageKey, JSON.stringify(filters));
  } catch (e) {
    console.error("Error writing date filters:", e);
  }
};
